import { useColumnsAgendamientos } from "@/erp/operaciones/agendamiento/shared/hooks";
import { EstadosAgendamientoEnumChoice, ModulosEnumChoice, newMotivoAgendaVentaSchema } from "@/shared";
import { useFetchAgendamientos, useFetchMotivosRechazo, useUpdateAgendamiento } from "@/shared/actions/erp";
import { CustomAutocomplete, CustomTextArea, GeneralTable } from "@/shared/components/ui";
import { ScrollableDialogProps } from "@/shared/components/ui/Dialogs";
import { useTableFilter } from "@/shared/hooks";
import { Agendamiento, FormMotivoRechazoAgendaVenta, MotivoRechazo } from "@/shared/interfaces";
import { yupResolver } from "@hookform/resolvers/yup";
import { Grid, IconButton, Tooltip } from "@mui/material";
import { useState } from "react";
import { useForm } from "react-hook-form";
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import UpdateIcon from '@mui/icons-material/Update';
import { useUiStore } from "@/store/ui";

interface AgendamientoAgendadoPageProps {
  es_pyme?: boolean
}

export default function PreRechazoAgendamientoVenta({
  es_pyme = false,
}: AgendamientoAgendadoPageProps) {

  ///* State
  const [modalCancelar, setModalCancelar] = useState(false);

  const setConfirmDialog = useUiStore(s => s.setConfirmDialog);
  const setConfirmDialogIsOpen = useUiStore(s => s.setConfirmDialogIsOpen);

  ///* Form
  const formRechazo = useForm<FormMotivoRechazoAgendaVenta>({
    resolver: yupResolver(newMotivoAgendaVentaSchema)
  });

  const {
    reset: resetRechazar,
  } = formRechazo;

  ///* Hook

  const { agendamientoColumnaVentas } = useColumnsAgendamientos();

  const {
    pagination,
    searchTerm,
    dateBefore,
    dateAfter,
    setPagination
  } = useTableFilter();

  const { pageIndex, pageSize } = pagination;

  ///* React Query

  const { isLoading, data: agendaRes,
    isRefetching, } = useFetchAgendamientos(
      {
        enabled: true,
        params: {
          estado_agendamiento: EstadosAgendamientoEnumChoice.PRERECHAZADO,
          identificacion: searchTerm,
          es_pyme,
          page: pageIndex + 1,
          page_size: pageSize,
          fecha_creacion_after: dateAfter,
          fecha_creacion_before: dateBefore
        }
      });

  const {
    data: motivosRechazoPaging,
    isLoading: isLoadingMotivosRechazo,
    isRefetching: isRefetchingMotivosRechazo,
  } = useFetchMotivosRechazo({
    estado: true,
    modulo: ModulosEnumChoice.AGENDAMIENTO,
    page_size: 100,
  });

  const { mutateAsync: mutateAsyncAgendamiento } = useUpdateAgendamiento<Partial<Agendamiento>>()

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12}>

        </Grid>
        <Grid item xs={12}>
          <GeneralTable<Agendamiento>
            columns={agendamientoColumnaVentas}
            data={agendaRes?.data.data || []}
            //
            isLoading={isLoading}
            isRefetching={isRefetching}
            // // search
            enableGlobalFilter={false}
            // onGlobalFilterChange={setGlobalFilter}
            // // pagination
            pagination={pagination}
            onPaging={setPagination}
            rowCount={agendaRes?.data?.count}
            // // actions
            actionsColumnSize={120}
            // crud
            positionActionsColumn="first"
            enableColumnActions={true}
            canEdit={false}
            //enableEditing={false}
            showCustomActionBtnSpace={true}
            customActionBtnSpace={(row) => {
              return (
                <Grid container>
                  <Grid item>
                    <Tooltip title={'Reversar'} placement="top">
                      <IconButton
                        onClick={() => {

                          setConfirmDialogIsOpen(true);
                          setConfirmDialog({
                            isOpen: true,
                            title: '¡Confirmar!',
                            subtitle:
                              'Estás a punto de reversar una venta de agendamiento a PYL. Este cambio es irreversible. ¿Te gustaría continuar?',
                            onClose() {
                              window.location.reload();
                            },
                            async onConfirm() {
                              setConfirmDialogIsOpen(false);
                              await mutateAsyncAgendamiento({
                                id: row.id_agendamiento!,
                                data: {
                                  estado_agendamiento: EstadosAgendamientoEnumChoice.FINALIZADO
                                },
                              });
                            },
                            cancelTextBtn: 'Cerrar',
                          });


                        }
                        }
                      >
                        <UpdateIcon />
                      </IconButton>
                    </Tooltip>
                  </Grid>

                  <Grid item>
                    <Tooltip title={'Cancelar'} placement="top">
                      <IconButton
                        onClick={() => {
                          setModalCancelar(true)
                          resetRechazar({
                            id_preagendamiento: row.preagendamiento_data?.id_preagendamiento!,
                            id_agendamiento: row.id_agendamiento,
                            identificacion: row.prospecto_data?.identificacion,
                            razon_social: row.prospecto_data?.razon_social,
                            observaciones: row.observaciones
                          })
                        }
                        }
                      >
                        <HighlightOffIcon color="error" />
                      </IconButton>
                    </Tooltip>
                  </Grid>

                </Grid>
              )
            }}

          />
        </Grid>
      </Grid>


      {/*Modal para cancelar*/}

      {modalCancelar && (
        <ScrollableDialogProps
          open={modalCancelar}
          title="¡Confirmar!"
          confirmVariantBtn="contained"
          dialogContentText={`Estás a punto de cancelar un agendamiento del cliente ${formRechazo.getValues().razon_social}
          con la identificación ${formRechazo.getValues().identificacion}. ¿Estás seguro? Este cambio es irreversible.`}
          contentNode={
            <Grid container spacing={3}>

              <CustomAutocomplete<MotivoRechazo>
                label="Motivo de rechazo"
                name="motivo_rechazo"
                // options
                options={motivosRechazoPaging?.data?.data || []}
                valueKey="nombre"
                actualValueKey="codigo"
                defaultValue={formRechazo.getValues().motivo_rechazo || ''}
                isLoadingData={
                  isLoadingMotivosRechazo || isRefetchingMotivosRechazo
                }
                // validation
                control={formRechazo.control}
                error={formRechazo?.formState?.errors.motivo_rechazo}
                helperText={
                  !!formRechazo?.formState?.errors.motivo_rechazo?.message &&
                  'El motivo de rechazo es requerido'
                }
                disableClearable
              />

              <CustomTextArea
                label="Observación"
                name="observacion_cancelacion"
                maxRows={3}
                control={formRechazo.control}
                defaultValue={formRechazo.getValues().observacion_cancelacion ?? ''}
                error={formRechazo.formState.errors.observacion_cancelacion}
                helperText={formRechazo.formState.errors.observacion_cancelacion?.message}
                required={false}
              />

            </Grid>
          }
          onConfirm={formRechazo.handleSubmit(async (form) => {
            const resp = await mutateAsyncAgendamiento({
              id: form.id_agendamiento,
              data: {
                estado_agendamiento: EstadosAgendamientoEnumChoice.CANCELADO,
                motivo_rechazo: form.motivo_rechazo,
                observaciones: {
                  ...form.observaciones,
                  observacion_cancelacion: form.observacion_cancelacion ?? ''
                }
              }
            });
            if (resp.code === 200) {
              resetRechazar({});
              setModalCancelar(false);
            }

          }, (error) => {
            console.log(error);

          })}
          onClose={() => {
            resetRechazar({});
            setModalCancelar(false)
          }}
        />
      )}


    </>
  );
};
